/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import type { Breakpoint } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import {
  AlertBanner,
  useConfirmationDialog,
  dateUtils,
} from '@opengeoweb/shared';
import { ContentDialog } from '../ContentDialog';
import LifeCycleDisplay from './LifeCycleDisplay';
import LifeCycleEdit from './LifeCycleEdit';
import type { SWEvent } from '../../types';
import { EventCategory, EventCategoryDetail } from '../../types';
import { useSpaceweatherTranslation } from '../../utils/i18n';

export const getDialogTitle = (
  t: TFunction,
  dialogMode: string,
  event: SWEvent,
): string => {
  const titleTime =
    event?.lifecycles!.internalprovider &&
    event.lifecycles!.internalprovider.draft !== true
      ? dateUtils.dateToString(
          dateUtils.utc(
            event.lifecycles!.externalprovider
              ? event.lifecycles!.externalprovider.firstissuetime
              : event.lifecycles!.internalprovider.firstissuetime,
          ),
          `${dateUtils.DATE_FORMAT_DATEPICKER}' UTC'`,
        )
      : 'Draft';

  if (dialogMode !== 'new') {
    if (event.categorydetail) {
      return `${
        EventCategoryDetail[
          event.categorydetail as keyof typeof EventCategoryDetail
        ]
      }: ${titleTime}`;
    }
    return `${
      EventCategory[event.category as keyof typeof EventCategory]
    }: ${titleTime}`;
  }
  return t('notification-title-new');
};

export interface LifeCycleDialogProps {
  open: boolean;
  toggleStatus: () => void;
  dialogMode?: string;
  event?: SWEvent;
  disableAutoFocus?: boolean;
}

const LifeCycleDialog: React.FC<LifeCycleDialogProps> = ({
  open,
  toggleStatus,
  dialogMode = 'new',
  event = null!,
  disableAutoFocus = false,
}: LifeCycleDialogProps) => {
  const { t } = useSpaceweatherTranslation();
  const confirmDialog = useConfirmationDialog();
  const [dialogSize, setDialogSize] = React.useState<Breakpoint>('lg');
  const [isFormChanged, setIsFormChanged] = React.useState(false);
  const [errorStoreMessage, setErrorStoreMessage] = React.useState('');
  const [errorRetrievePopulate, setErrorRetrievePopulate] = React.useState('');

  const onChangeForm = (hasChanged: boolean): void => {
    setIsFormChanged(hasChanged);
  };

  React.useEffect(() => {
    if (dialogMode === 'new' || event?.originator === 'KNMI') {
      setDialogSize('sm');
    } else {
      setDialogSize('lg');
    }
  }, [dialogMode, event]);

  const onToggleDialog = (formSaved = false): void => {
    if (isFormChanged && !formSaved) {
      void confirmDialog({
        title: t('close-dialog-title'),
        description: t('close-dialog-description'),
        confirmLabel: t('close-dialog-confirm'),
        cancelLabel: t('dialog-cancel'),
      }).then(() => {
        toggleStatus();
      });
    } else {
      toggleStatus();
    }
  };

  return (
    <ContentDialog
      alertBanner={
        errorStoreMessage || errorRetrievePopulate ? (
          <AlertBanner
            info={errorStoreMessage || errorRetrievePopulate}
            severity="error"
            title={
              errorStoreMessage
                ? t('dialog-save-error')
                : t('dialog-template-error')
            }
          />
        ) : null
      }
      data-testid="lifecycle-dialog"
      disableAutoFocus={disableAutoFocus}
      fullWidth
      maxWidth={dialogSize}
      onClose={onToggleDialog}
      open={open}
      title={getDialogTitle(t, dialogMode, event)}
      toggleStatus={onToggleDialog}
    >
      {dialogMode === 'new' && (
        <LifeCycleEdit
          baseNotificationType="new"
          onFormChange={onChangeForm}
          setErrorRetrievePopulate={setErrorRetrievePopulate}
          setErrorStoreMessage={setErrorStoreMessage}
          statusTagContent="Alert"
          toggleDialogOpen={onToggleDialog}
        />
      )}
      {dialogMode !== 'new' && (
        <LifeCycleDisplay
          eventId={dialogMode}
          onFormChange={onChangeForm}
          setErrorRetrievePopulate={setErrorRetrievePopulate}
          setErrorStoreMessage={setErrorStoreMessage}
          toggleDialogOpen={onToggleDialog}
        />
      )}
    </ContentDialog>
  );
};

export default LifeCycleDialog;
